import {
  getDocs,
  getDocsFromServer,
  type DocumentData,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore';

/**
 * `getDocs`, but a cache answer is never mistaken for the server's.
 *
 * `getDocs` resolves with the first snapshot its listener sees. If Firestore
 * has not reached the server yet — the first seconds after launch, or a
 * connection attempt that just failed — that snapshot comes from the local
 * cache, and an empty cache reads as "you have no sessions". It happened on
 * Progress right after the splash.
 *
 * So a cache answer triggers an explicit server read, bounded by `timeoutMs`:
 *  - server answers        → use it (`provisional: false`)
 *  - no server, cache rows  → show the cache, flagged `provisional` so the
 *                             screen refetches next time
 *  - no server, empty cache → throw: "could not load" is true, "nothing
 *                             there" might not be
 */
export interface ServerFirstResult<T> {
  snapshot: QuerySnapshot<T>;
  provisional: boolean;
}

export class OfflineEmptyError extends Error {
  constructor() {
    super('Could not reach the server, and nothing is saved on this device yet.');
    this.name = 'OfflineEmptyError';
  }
}

export async function getDocsServerFirst<T = DocumentData>(
  q: Query<T>,
  timeoutMs = 8000,
): Promise<ServerFirstResult<T>> {
  const first = await getDocs(q);
  if (!first.metadata.fromCache) return { snapshot: first, provisional: false };

  console.info(`[firestore] answer came from cache (${first.size} docs); asking the server`);
  try {
    const server = await Promise.race([
      getDocsFromServer(q),
      new Promise<never>((_, reject) => window.setTimeout(() => reject(new Error('timeout')), timeoutMs)),
    ]);
    return { snapshot: server, provisional: false };
  } catch {
    if (first.size > 0) return { snapshot: first, provisional: true };
    throw new OfflineEmptyError();
  }
}
